import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, AlertCircle, Users, RefreshCw } from 'lucide-react';
import { adminAPI } from '../../../services/adminAPI';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import Badge from '../../../components/common/Badge';
import Alert from '../../../components/common/Alert';
import AddUserModal from './components/AddUserModal';
import EditUserModal from './components/EditUserModal';
import Tooltip from '../../../components/common/Tooltip';
import { supabase } from '../../../config/supabase';

export default function UsersManagement() {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, organizationId, permissions } = useAuth();
  const queryClient = useQueryClient();

  // Fetch users directly from Supabase instead of the API
  const { 
    data: users = [], 
    isLoading: isLoadingUsers, 
    isError: isUserError,
    error: userError,
    refetch: refetchUsers
  } = useQuery({
    queryKey: ['adminUsers', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        console.error('Organization ID not available for users query');
        throw new Error('Organization ID not available');
      }
      
      // Store organizationId in localStorage for potential use in API calls
      localStorage.setItem('organizationId', organizationId);
      
      console.log(`Fetching users for organization: ${organizationId}`);
      
      try {
        // Direct Supabase query - similar to the one working in network trace
        const { data, error } = await supabase
          .from('organization_members')
          .select(`
            id,
            organization_id,
            role,
            has_seat,
            user_id,
            users:user_id (
              id,
              email,
              name,
              status,
              created_at,
              updated_at,
              job_title,
              department,
              smtp_routing_enabled,
              smtp_configured,
              oauth_token_expiry
            )
          `)
          .eq('organization_id', organizationId);
          
        if (error) {
          console.error('Supabase error fetching users:', error);
          throw error;
        }
        
        // Transform data to match expected format in component
        const transformedUsers = (data || [])
          .filter(member => member.users) // Ensure we have user data
          .map(member => {
            const userData = member.users;
            return {
              id: userData.id,
              email: userData.email,
              name: userData.name || 'Unknown User',
              status: userData.status,
              is_domain_admin: member.role === 'admin',
              created_at: userData.created_at,
              updated_at: userData.updated_at,
              last_login: userData.oauth_token_expiry || userData.updated_at, // Deriving last_login
              job_title: userData.job_title || '',
              department: userData.department || '',
              permissions: {},
              smtp_routing_enabled: userData.smtp_routing_enabled,
              smtp_configured: userData.smtp_configured,
              has_seat: member.has_seat,
              organization_member_id: member.id
            };
          });
        
        console.log(`Successfully fetched ${transformedUsers.length} users for organization ${organizationId}`);
        
        return transformedUsers;
      } catch (err) {
        console.error('Error fetching users:', err);
        throw err;
      }
    },
    enabled: !!organizationId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30000, // Reduced to 30 seconds
    cacheTime: 5 * 60 * 1000,
    retry: 3,  
    retryDelay: 1000,
  });

  // Also add this useEffect for better handling of organizationId changes
  useEffect(() => {
    if (organizationId) {
      console.log('Organization ID changed or initialized:', organizationId);
      
      // Force a refetch when organizationId changes
      queryClient.removeQueries(['adminUsers']);
      refetchUsers();
    }
  }, [organizationId, queryClient, refetchUsers]);

  // Fetch subscription info
  const { 
    data: subscription = { seats_allocated: 0, seats_used: 0, status: 'inactive' },
    isLoading: isLoadingSubscription
  } = useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: async () => {
      try {
        const response = await adminAPI.getSubscription();
        
        // If we have subscription data, return it
        if (response.data?.subscription) {
          return response.data.subscription;
        }
        
        // Otherwise return a default object
        return {
          seats_allocated: 0,
          seats_used: 0,
          status: 'inactive'
        };
      } catch (err) {
        // Return default data to prevent UI errors
        return {
          seats_allocated: 0,
          seats_used: 0,
          status: 'inactive'
        };
      }
    },
    refetchOnWindowFocus: false,
    // Only run if we have an organization ID
    enabled: !!organizationId
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId) => adminAPI.deleteUser(userId),
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries(['adminUsers', organizationId]);
      queryClient.invalidateQueries(['subscription', organizationId]);
    },
    onError: (error) => {
      console.error('Failed to delete user:', error);
      // Optionally show an error toast/alert here
    }
  });

  // Calculate seat information - fallback to derived values if API doesn't provide it
  const totalSeats = subscription?.seats_allocated || 0;
  // If the subscription has seats_used, use it, otherwise count users with has_seat = true
  const usedSeats = subscription?.seats_used !== undefined 
    ? subscription.seats_used 
    : Array.isArray(users) ? users.filter(user => user.has_seat).length : 0;
  const seatsRemaining = subscription?.seats_remaining !== undefined
    ? subscription.seats_remaining
    : Math.max(0, totalSeats - usedSeats);
  const isNearSeatLimit = seatsRemaining <= 2;
  const hasNoSeats = seatsRemaining <= 0;

  // Filter users based on the search query
  const filteredUsers = React.useMemo(() => {
    if (!Array.isArray(users)) {
      console.warn('Users is not an array:', users);
      return [];
    }
    
    return users.filter(user => {
      if (!user) return false;
      
      const searchLower = searchQuery.toLowerCase();
      return (
        (user.email || '').toLowerCase().includes(searchLower) ||
        (user.name || '').toLowerCase().includes(searchLower)
      );
    });
  }, [users, searchQuery]);

  // User management handlers
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUserMutation.mutateAsync(userId);
    } catch (error) {
      console.error('Failed to delete user:', error);
      // Error is handled by the mutation's onError
    }
  };

  // Manual refresh handler
  const handleManualRefresh = async () => {
    try {
      console.log('Forcing full data refresh...');
      
      // Clear React Query cache for users and subscription
      queryClient.removeQueries(['adminUsers', organizationId]);
      queryClient.removeQueries(['subscription', organizationId]);
      
      // Fetch fresh data
      await refetchUsers();
      
      // After users are fetched, also refetch subscription data
      await queryClient.refetchQueries(['subscription', organizationId]);
      
      console.log('Full refresh completed successfully');
    } catch (error) {
      console.error('Error during forced refresh:', error);
    }
  };

  // Date formatter helper
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Seat Management */}
      <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center">
        <div>
          <h1 className="text-2xl font-semibold text-midnight font-heading">User Management</h1>
          <p className="text-sm text-ebony">
            Manage your organization's users and seat allocations
          </p>
        </div>
        
        {/* Seat Information */}
        <div className="flex items-center space-x-4">
          <div className="bg-white rounded-xl p-3 shadow-md border border-ebony border-opacity-20">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-violet" />
              <div>
                <p className="text-sm text-ebony">Seats Used</p>
                <p className="font-semibold text-midnight">
                  {usedSeats} / {totalSeats}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={handleManualRefresh} 
              title="Refresh user list"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            
            <Tooltip 
              content={hasNoSeats ? "No seats available. Please upgrade your subscription." : ""}
              disabled={!hasNoSeats}
            >
              <Button 
                onClick={() => setIsAddUserModalOpen(true)}
                disabled={hasNoSeats}
              >
                <span className="flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </span>
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {isLoadingUsers ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet"></div>
        </div>
      ) : isUserError ? (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-midnight font-heading">User Management</h1>
            <Button onClick={handleManualRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
          
          <Alert type="error">
            <AlertCircle className="w-4 h-4 mr-2" />
            {userError?.message || 'Failed to load users. Please try again.'}
          </Alert>
        </div>
      ) : (
        <>
          {/* Alerts */}
          {isNearSeatLimit && (
            <Alert type="warning">
              <AlertCircle className="w-4 h-4 mr-2" />
              {hasNoSeats 
                ? 'You have reached your seat limit. Please upgrade your subscription to add more users.'
                : `Running low on seats! Only ${seatsRemaining} seat${seatsRemaining === 1 ? '' : 's'} remaining.`
              }
              {hasNoSeats && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-4"
                  onClick={() => window.location.href = '/admin/billing'}
                >
                  Upgrade Subscription
                </Button>
              )}
            </Alert>
          )}

          {/* No users message if array is empty */}
          {Array.isArray(users) && users.length === 0 && !isLoadingUsers && (
            <Alert type="info">
              <AlertCircle className="w-4 h-4 mr-2" />
              No users found. Add your first user to get started.
            </Alert>
          )}

          {/* Main Card */}
          <Card>
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ebony w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-pill border border-ebony border-opacity-30 focus:ring-2 focus:ring-sky-blue/20 focus:border-sky-blue transition-colors"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ebony border-opacity-20">
                    <th className="text-left py-3 px-4 text-midnight font-heading">User</th>
                    <th className="text-left py-3 px-4 text-midnight font-heading">Role</th>
                    <th className="text-left py-3 px-4 text-midnight font-heading">Status</th>
                    <th className="text-left py-3 px-4 text-midnight font-heading">Seat</th>
                    <th className="text-left py-3 px-4 text-midnight font-heading">Last Login</th>
                    <th className="text-right py-3 px-4 text-midnight font-heading">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    if (!user || !user.id) {
                      console.warn('Invalid user object found:', user);
                      return null;
                    }
                    // Calculate role based on the is_domain_admin field
                    const role = user.is_domain_admin ? 'admin' : 'member';
                    return (
                      <tr key={user.id} className="border-b border-ebony border-opacity-10 hover:bg-old-lace/20">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=e0d5eb&color=5E3258`}
                              alt={user.name}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <div className="font-medium text-midnight">{user.name || 'Unnamed User'}</div>
                              <div className="text-sm text-ebony">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={role === 'admin' ? 'success' : 'info'}>
                            {role === 'admin' ? 'Admin' : 'Member'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={
                              user.status === 'pending' 
                                ? 'warning' 
                                : user.status === 'active' 
                                  ? 'success' 
                                  : 'error'
                            }
                          >
                            {user.status === 'pending' 
                              ? 'Pending Setup' 
                              : user.status.charAt(0).toUpperCase() + user.status.slice(1)
                            }
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {user.has_seat ? (
                            <Badge variant="success">Yes</Badge>
                          ) : (
                            <Badge variant="error">No</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-ebony">
                          {formatDateTime(user.last_login)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={role === 'admin' && filteredUsers.filter(u => u.is_domain_admin).length === 1}
                              title={
                                role === 'admin' && filteredUsers.filter(u => u.is_domain_admin).length === 1
                                  ? "Cannot delete the last admin"
                                  : "Delete user"
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-ebony">
                  {searchQuery 
                    ? 'No users found matching your search'
                    : 'No users found in your organization'
                  }
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Modals */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries(['adminUsers', organizationId]);
          queryClient.invalidateQueries(['subscription', organizationId]);
          setIsAddUserModalOpen(false);
        }}
      />
      
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={() => {
          queryClient.invalidateQueries(['adminUsers', organizationId]);
          setIsEditModalOpen(false);
        }}
      />
    </div>
  );
}